import { QuoteItemsAttributesPatchRequestBody } from '@lio/api-types';
import { getUnsavedAttributeValue } from '@pages/QuoteDetails/helpers/getUnsavedAttributeValue';
import { useUpdateQuoteItemAttributes } from '@services/quoteItem';
import { setDateToMidnight } from '@utils/date';
import { pick } from 'lodash';
import { useMemo, useCallback } from 'react';
import { shallow } from 'zustand/shallow';
import { useQuoteStore } from '@store/Quote/QuoteStore';

export function useSaveFields({
  quoteId,
  quoteItemId,
  fields,
}: {
  quoteId: string;
  quoteItemId: string;
  fields: {
    attributeTypeId: string;
    attributeId?: string;
    dataType?: string;
    uiFieldType?: string;
  }[];
}) {
  const fieldRefs = fields.map((f) => f.attributeId ?? `${quoteItemId}-${f.attributeTypeId}`);
  const storedAttributes = useQuoteStore((state) => pick(state.attributesById, fieldRefs), shallow);
  const clearAttributeValues = useQuoteStore((state) => state.clearAttributeValues, shallow);
  const updateAttributesMutation = useUpdateQuoteItemAttributes();
  const unsavedFields = useMemo(
    () =>
      fields.filter(
        (f) =>
          getUnsavedAttributeValue(
            storedAttributes,
            { id: f.attributeTypeId, attribute: { id: f.attributeId } },
            quoteItemId,
            'value',
          ) !== undefined ||
          getUnsavedAttributeValue(
            storedAttributes,
            { id: f.attributeTypeId, attribute: { id: f.attributeId } },
            quoteItemId,
            'valueOverride',
          ) !== undefined,
      ),
    [fields, quoteItemId, storedAttributes],
  );

  const saveFields = useCallback(
    (
      attributes?: {
        id?: string;
        attributeTypeId: string;
        value: string | number | boolean | [] | {};
        valueOverride?: string | number | boolean | [] | {};
        isUpdatedByUser?: boolean;
      }[],
      shouldInvalidateQueries = true,
    ) => {
      // Replace any stored unsaved values with directly invoked values
      const unsavedAttributes = unsavedFields
        .flatMap((f) => {
          if (attributes?.find((attribute) => attribute.attributeTypeId === f.attributeTypeId)) {
            return [];
          }
          const value = getUnsavedAttributeValue(
            storedAttributes,
            {
              id: f.attributeTypeId,
              attribute: { id: f.attributeId },
              dataType: f.dataType,
              uiFieldType: f.uiFieldType,
            },
            quoteItemId,
            'value',
          );

          const isUpdatedByUser = getUnsavedAttributeValue(
            storedAttributes,
            {
              id: f.attributeTypeId,
              attribute: { id: f.attributeId },
              dataType: f.dataType,
              uiFieldType: f.uiFieldType,
            },
            quoteItemId,
            'isUpdatedByUser',
          );

          const valueOverride = getUnsavedAttributeValue(
            storedAttributes,
            {
              id: f.attributeTypeId,
              attribute: { id: f.attributeId },
              dataType: f.dataType,
              uiFieldType: f.uiFieldType,
            },
            quoteItemId,
            'valueOverride',
          );
          return [
            {
              isUpdatedByUser: isUpdatedByUser,
              value:
                value && f.dataType === 'DateTime' && value instanceof Date
                  ? setDateToMidnight(value as unknown as Date).toJSON()
                  : value,
              valueOverride:
                valueOverride && f.dataType === 'DateTime' && valueOverride instanceof Date
                  ? setDateToMidnight(valueOverride as unknown as Date).toJSON()
                  : valueOverride,
              id: f.attributeId,
              quoteItemAttributeTypeId: f.attributeTypeId,
            },
          ];
        })
        .concat(
          attributes?.map((attribute) => ({
            value: attribute.value,
            valueOverride: attribute.valueOverride,
            id: attribute.id,
            quoteItemAttributeTypeId: attribute.attributeTypeId,
            isUpdatedByUser: attribute.isUpdatedByUser,
          })) ?? [],
        );

      const request = {
        quoteId,
        quoteItems: [
          {
            id: quoteItemId,
            quoteItemAttributes: unsavedAttributes,
          },
        ] as QuoteItemsAttributesPatchRequestBody['quoteItems'],
      };
      return updateAttributesMutation.mutateAsync({ ...request, shouldInvalidateQueries }).then(() => {
        setTimeout(() => {
          // Put call at end of call stack to allow other query updates to resolve first
          // otherwise we get a flash of old value before new server value is read
          clearAttributeValues(unsavedFields.map((f) => f.attributeId ?? `${quoteItemId}-${f.attributeTypeId}`));
        }, 0);
      });
    },
    [clearAttributeValues, quoteId, quoteItemId, storedAttributes, unsavedFields, updateAttributesMutation],
  );
  return {
    saveFields,
    unsavedFields,
    isLoading: updateAttributesMutation.isPending,
  };
}
